import { Attachment } from "@/api/attachment";
import { Instance } from "@/context/auth/AuthContext";
import { SupportedLocale } from "@/i18n";
import {
  hasValidDimensions,
  isPhotoModuleEnabled,
} from "@/lib/compute-field/artwork-module-helpers";
import {
  computeFieldArtworkOverviewSummary,
  computeFieldLocalisationHistoriqueArtwork,
  computeFieldRelatedAuctions,
  computeFieldRelatedAuthorsArtwork,
  computeFieldRelatedBibliographies,
  computeFieldRelatedExhibitions,
  computeFieldTirageHistory,
} from "@/lib/computeFields";
import { getDefaultAttachment } from "@/lib/image";
import {
  mapTirageInAuctionsToArtwork,
  mapTirageInBibliographiesToArtwork,
  mapTirageInExhibitionsToArtwork,
} from "@/lib/tirage-relation-mappers";
import { Artwork } from "@/lib/types";
import { ComputedFields, MergedDisplayData } from "@/pages/dashboard/artwork-overview/types";
import { useMemo } from "react";

export interface UseArtworkPageDataProps {
  data: Artwork & { tirageData?: any };
  attachments: Attachment[];
  instanceData: Instance | null;
  locale: SupportedLocale;
  t: (key: string, options?: any) => string;
  filterFromGeneralCR: {
    showPublicAuctions: boolean;
  };
  itemType?: "ARTWORK" | "PHOTO_MASTER" | "TIRAGE";
}

export interface UseArtworkPageDataResult {
  displayData: MergedDisplayData;
  mainImage: Attachment | null;
  otherImages: Attachment[];
  isTirage: boolean;
  isPhotoMaster: boolean;
  tirageData: any;
  isPhotoModuleEnabledValue: boolean;
  hasValidDimensionsValue: boolean;
  computedFields: ComputedFields;
}

function determineItemType(
  itemType?: "ARTWORK" | "PHOTO_MASTER" | "TIRAGE",
  tirageData?: any
): { isTirage: boolean; isPhotoMaster: boolean } {
  const isTirage = itemType === "TIRAGE" || !!tirageData;
  const isPhotoMaster = itemType === "PHOTO_MASTER";

  return {
    isTirage,
    isPhotoMaster,
  };
}

function mergeDisplayData(
  data: Artwork & { tirageData?: any },
  isTirage: boolean,
  tirageData?: any
): MergedDisplayData {
  if (!isTirage) return data as MergedDisplayData;

  // Merge Tirage fields into Artwork structure for compatibility
  return {
    ...data,
    // Use Tirage numeroEnregistrement if available, otherwise use Artwork
    oeuvresDescriptionNumeroEnregistrement:
      tirageData?.tirageDescriptionNumeroEnregistrement ||
      data.oeuvresDescriptionNumeroEnregistrement,
    // Use Tirage date if available
    oeuvresDescriptionDateDeCreation:
      tirageData?.tirageDescriptionDateCreation ||
      data.oeuvresDescriptionDateDeCreation,
    // Use Tirage technique if available
    oeuvresDescriptionTechnique:
      tirageData?.tirageDescriptionTechnique || data.oeuvresDescriptionTechnique,
    // Use Tirage support if available
    oeuvresDescriptionSupport:
      tirageData?.tirageDescriptionSupport || data.oeuvresDescriptionSupport,
    // Use Tirage dimensions WITH margins if available (convert JSONB to string)
    // Fallback to dimensions without margins, then to Artwork dimensions
    oeuvresDescriptionDimensionsOeuvre: tirageData?.tirageDescriptionDimensionsWithMargins
      ? typeof tirageData.tirageDescriptionDimensionsWithMargins === "string"
        ? tirageData.tirageDescriptionDimensionsWithMargins
        : JSON.stringify(tirageData.tirageDescriptionDimensionsWithMargins)
      : tirageData?.tirageDescriptionDimensionsWithoutMargins
        ? typeof tirageData.tirageDescriptionDimensionsWithoutMargins === "string"
          ? tirageData.tirageDescriptionDimensionsWithoutMargins
          : JSON.stringify(tirageData.tirageDescriptionDimensionsWithoutMargins)
        : data.oeuvresDescriptionDimensionsOeuvre,
    // Use Tirage exhibitions/auctions/bibliography if available
    // Normalize TirageIn* relations to ArtworkIn* format for compute fields
    artworkInExhibitions:
      mapTirageInExhibitionsToArtwork(tirageData?.tirageInExhibition) ??
      data.artworkInExhibitions,
    artworkInAuctions:
      mapTirageInAuctionsToArtwork(tirageData?.tirageInAuction) ??
      data.artworkInAuctions,
    artworkInBibliography:
      mapTirageInBibliographiesToArtwork(tirageData?.tirageInBibliography) ??
      data.artworkInBibliography,
    // Keep artworkHistory for Artwork, and add tirageHistory separately
    artworkHistory: data.artworkHistory,
    // Add tirageHistory if available (will be used separately)
    tirageHistory: (tirageData?.tirageHistory || []) as any[],
  } as MergedDisplayData;
}

export function useArtworkPageData({
  data,
  attachments,
  instanceData,
  locale,
  t,
  filterFromGeneralCR,
  itemType,
}: UseArtworkPageDataProps): UseArtworkPageDataResult {
  // All hooks must be called before any conditional returns
  const { isTirage, isPhotoMaster } = useMemo(
    () => determineItemType(itemType, data?.tirageData),
    [itemType, data?.tirageData]
  );
  
  const tirageData = useMemo(() => data?.tirageData, [data?.tirageData]);

  const displayData = useMemo(
    () => (data ? mergeDisplayData(data, isTirage, tirageData) : ({} as MergedDisplayData)),
    [data, isTirage, tirageData]
  );

  const mainImage = useMemo(
    () => getDefaultAttachment(attachments, "artwork"),
    [attachments]
  );

  const otherImages = useMemo(
    () =>
      attachments
        .filter((attachment) => attachment.id !== mainImage?.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0)),
    [attachments, mainImage]
  );

  const isPhotoModuleEnabledValue = useMemo(
    () =>
      displayData && instanceData
        ? isPhotoModuleEnabled(displayData, instanceData, locale)
        : false,
    [displayData, instanceData, locale]
  );

  const hasValidDimensionsValue = useMemo(
    () => (displayData ? hasValidDimensions(displayData) : false),
    [displayData]
  );

  // Compute fields
  const historyCalculated = useMemo(
    () =>
      displayData
        ? computeFieldLocalisationHistoriqueArtwork(displayData, {
            language: locale,
          })
        : "",
    [displayData, locale]
  );

  const tirageHistoryCalculated = useMemo(() => {
    if (!isTirage || !displayData?.tirageHistory || displayData.tirageHistory.length === 0) {
      return null;
    }
    return computeFieldTirageHistory(
      { tirageHistory: displayData.tirageHistory },
      { language: locale, translation: t, lineBreak: true },
    );
  }, [isTirage, displayData?.tirageHistory, locale, t]);

  const exhibitionsCalculated = useMemo(
    () =>
      displayData?.artworkInExhibitions
        ? computeFieldRelatedExhibitions(
            { relatedExhbitions: displayData.artworkInExhibitions },
            { language: locale }
          )
        : "",
    [displayData?.artworkInExhibitions, locale]
  );

  const auctionCalculated = useMemo(() => {
    if (!filterFromGeneralCR.showPublicAuctions || !displayData?.artworkInAuctions) return null;
    return computeFieldRelatedAuctions(
      { relatedAuctions: displayData.artworkInAuctions },
      { language: locale }
    );
  }, [filterFromGeneralCR.showPublicAuctions, displayData?.artworkInAuctions, locale]);

  const bibliographyCalculated = useMemo(
    () =>
      displayData?.artworkInBibliography
        ? computeFieldRelatedBibliographies(
            { relatedBibliographies: displayData.artworkInBibliography },
            { language: locale }
          )
        : "",
    [displayData?.artworkInBibliography, locale]
  );

  const artistsCalculated = useMemo(
    () => (displayData ? computeFieldRelatedAuthorsArtwork(displayData, { language: locale }) : ""),
    [displayData, locale]
  );

  const overviewSummaryCalculated = useMemo(
    () =>
      displayData
        ? computeFieldArtworkOverviewSummary(
            {
              ...displayData,
              preposition: t("computeField.techniqueSupportPreposition"),
              doubleFace: t("computeField.doubleFace"),
              t: t,
            },
            {
              language: locale,
              isPhotoModuleEnabled: isPhotoModuleEnabledValue,
              hasValidDimensions: hasValidDimensionsValue,
            }
          )
        : "",
    [displayData, locale, t, isPhotoModuleEnabledValue, hasValidDimensionsValue]
  );

  const computedFields: ComputedFields = useMemo(
    () => ({
      historyCalculated,
      tirageHistoryCalculated,
      exhibitionsCalculated,
      auctionCalculated,
      bibliographyCalculated,
      artistsCalculated,
      overviewSummaryCalculated,
    }),
    [
      historyCalculated,
      tirageHistoryCalculated,
      exhibitionsCalculated,
      auctionCalculated,
      bibliographyCalculated,
      artistsCalculated,
      overviewSummaryCalculated,
    ]
  );

  // Now we can safely return, handling the null data case
  if (!data) {
    return {
      displayData: {} as MergedDisplayData,
      mainImage: (mainImage ?? null) as Attachment | null,
      otherImages: (otherImages ?? []) as Attachment[],
      isTirage: false,
      isPhotoMaster: false,
      tirageData: undefined,
      isPhotoModuleEnabledValue: false,
      hasValidDimensionsValue: false,
      computedFields: {
        historyCalculated: "",
        tirageHistoryCalculated: null,
        exhibitionsCalculated: "",
        auctionCalculated: null,
        bibliographyCalculated: "",
        artistsCalculated: "",
        overviewSummaryCalculated: "",
      },
    };
  }

  return {
    displayData,
    mainImage: mainImage as Attachment | null,
    otherImages: otherImages as Attachment[],
    isTirage,
    isPhotoMaster,
    tirageData,
    isPhotoModuleEnabledValue,
    hasValidDimensionsValue,
    computedFields,
  };
}

